import type { Merge } from 'type-fest';
import { isPresent } from 'utils/isPresent';
import sanitizeObjectEntries from 'utils/sanitizeObjectData';

import { FixIt } from '@/Resources';
import { CustomerApiResponse } from 'components/CustomerIntake/types/customerApiResponse';
import { AddressApiPayload } from 'components/Customers/api/AddressesApi';

import RestAPI from '../../Common/restApi';

export const ROWS_PER_PAGE = 100;

export type CustomerUpdatePayload = Merge<
  Partial<CustomerApiResponse>,
  { billing_address: AddressApiPayload | null; addresses: AddressApiPayload[] }
>;

type UpdateCustomerPayload = {
  first_name?: Nullable<string>;
  last_name?: Nullable<string>;
  home_number?: Nullable<string>;
  notifications_enabled?: Nullable<boolean>;
};

export type CreateOrUpdateCustomerResponse = {
  customer: {
    id: string;
    uuid: string;
    first_name: string;
    full_name: string;
    service_addresses: unknown[];
    printable_address: string;
    tags: unknown[];
    notifications_enabled: boolean;
  };
};

const CustomersApi = {
  exportCustomers: () => {
    return RestAPI.postWithFormData('/pro/customers/export', null);
  },
  mergeCustomers: (data: FixIt) => {
    return RestAPI.postWithFormData('/pro/customers/merge', data);
  },
  getCustomers(page: number) {
    return RestAPI.getWithFormData('/pro/customers/get_customers/', {
      page,
      row_limit: ROWS_PER_PAGE,
    });
  },
  getRecommendedCustomers(page: number, pageSize: number, sortBy: string, sortDirection: string) {
    return RestAPI.getWithFormData('/api/v2/reviews/recommended_customers', {
      page,
      page_size: pageSize,
      sort_by: sortBy,
      sort_direction: sortDirection,
    });
  },
  searchCustomers(page: number, searchTerm: string, filterTagIds: string[], leadSourceUUIds: string[]) {
    return RestAPI.postWithFormData('/pro/customers/search', {
      row_limit: ROWS_PER_PAGE,
      page,
      search_term: isPresent(searchTerm) ? searchTerm : '',
      filter_lead_source_uuids: leadSourceUUIds || [],
      filter_tag_ids: filterTagIds || [],
    });
  },

  importCustomersFromGoogle(customers: FixIt) {
    return RestAPI.postWithFormData('/api/customers/import', { customer_list: customers });
  },

  get(id: string) {
    return RestAPI.get<CustomerApiResponse>(`/api/v2/pro/customers/${id}`);
  },

  delete(id: number) {
    return RestAPI.delete(`/api/v2/pro/customers/${id}`);
  },

  update(id: number, data: CustomerUpdatePayload) {
    const sanitizedData = sanitizeObjectEntries(data);
    return RestAPI.updateWithFormData<CustomerApiResponse, CustomerUpdatePayload>(
      `/api/v2/pro/customers/${id}`,
      sanitizedData
    );
  },

  addJob(customerId: string, data: FixIt) {
    return RestAPI.postWithFormData(`/pro/add_job/customer/${customerId}`, data);
  },

  addEstimate(customerId: string, addressId: string, isVirtual: boolean) {
    return RestAPI.postWithFormData(`/pro/add_estimate/customer/${customerId}`, {
      address_id: addressId,
      is_virtual: isVirtual,
    });
  },

  saveSortOrderAndGetCustomers(data: FixIt) {
    return RestAPI.postWithFormData('/pro/customers/save_sort_order_and_get_customers', data);
  },

  importCustomerListCSV(data: FixIt) {
    return RestAPI.postWithFormData('/pro/customers/import_csv', data);
  },

  restoreCustomer(customerId: string) {
    return RestAPI.postWithFormData(`/pro/customers/restore/${customerId}`, null);
  },

  addTags(data: FixIt) {
    return RestAPI.postWithFormData('/pro/customers/add_customer_list_tag', data);
  },

  deleteCustomers(data: FixIt) {
    return RestAPI.deleteWithFormData('/pro/customers/delete_customers', data);
  },

  createOrUpdateCustomer(formValues: UpdateCustomerPayload) {
    const sanitizedFormValues = sanitizeObjectEntries(formValues);
    const requestUrl = '/pro/customers';
    return RestAPI.postWithFormData<CreateOrUpdateCustomerResponse, UpdateCustomerPayload>(
      requestUrl,
      sanitizedFormValues
    );
  },

  getCompanyCountry() {
    const requestUrl = '/pro/customer/company_country';
    return RestAPI.get(requestUrl);
  },

  searchCustomer(queryString: string, customerId?: string | null) {
    const isQuery = Boolean(queryString);
    const isCustomerId = Boolean(customerId);

    const query = isQuery ? `q=${encodeURIComponent(queryString)}` : '';
    const customerIdQuery = isCustomerId ? `uid=${customerId}` : '';

    const params = [query, customerIdQuery].filter(Boolean).join('&');
    const requestUrl = `/pro/customers/search?${params}`;

    return RestAPI.get<Array<{ id: number }>>(requestUrl);
  },

  saveUiConfig(data: FixIt) {
    const requestUrl = `/pro/customers/save_ui_config`;
    return RestAPI.postWithFormData(requestUrl, data);
  },

  getInvoiceHistory(id: number) {
    return RestAPI.get(`/api/v2/pro/customers/${id}/invoice_history`);
  },

  getRequestAddresses(id: number) {
    return RestAPI.get(`/api/v2/pro/customers/${id}/request_addresses`);
  },

  getFilterableData() {
    const requestUrl = '/api/v2/pro/customers/get_filterable_data';
    return RestAPI.get(requestUrl);
  },

  getCustomerPaymentStats(id: number) {
    const requestUrl = `/api/v2/pro/customers/${id}/payment_stats`;
    return RestAPI.get(requestUrl);
  },
};

export default CustomersApi;
